// -----------------------------------------------------------------------------
//
// This file is the copyrighted property of Tableau Software and is protected
// by registered patents and other applicable U.S. and international laws and
// regulations.
//
// Unlicensed use of the contents of this file is prohibited. Please refer to
// the NOTICES.txt file for further details.
//
// -----------------------------------------------------------------------------
import * as _ from 'underscore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { appStore } from '../../app/AppStore';
import { ExtractEncryptionState, IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { IExtractItemInfo } from '../../ts/models/resourceInfoParts';
import { Module } from '../../ts/Module';
import { IPollExtractActionStatusResult, PollExtractActionStatusRequest } from '../../ts/ServerApi/requests/PollExtractActionStatusRequest';
import { CreateExtractTasksAction, ExtractActionType } from '../../ts/services/CreateExtractTasksAction';
import { IPoller, PollService } from '../../ts/services/PollService';
import { ServiceLocator } from '../../app/ServiceLocator';
import { toNativePromise } from '../../commonInfra/utils/QUtils';

let statusPoller: IPoller<IPollExtractActionStatusResult>;

function pollAndUpdateExtractActionStatus(contentItem: IWorkbookInfo | IDataSourceInfo) {

  if (!(contentItem as IExtractItemInfo).extractCreationPending &&
  (contentItem as IExtractItemInfo).extractEncryptionState !== ExtractEncryptionState.pendingDecryption &&
  (contentItem as IExtractItemInfo).extractEncryptionState !== ExtractEncryptionState.pendingEncryption) {
  // no pending status, do nothing
    return;
  }

  let description: string;

  if(contentItem.extractCreationPending) {
    description = ExtractActionType.createExtracts;
  }
  else if (contentItem.extractEncryptionState === ExtractEncryptionState.pendingEncryption) {
    description = ExtractActionType.encryptExtract;
  }
  else {
    description = ExtractActionType.decryptExtract;
  }

  const actionType = description;

  pollExtractActionStatus(contentItem.id, contentItem.contentType, actionType).then(
    (result: IPollExtractActionStatusResult) => {
    //completed
    completeExtractStatusPolling(result,contentItem,actionType);
    },
    // error, if error happened, stop to poll the pending status,
    () => {
    cancelExtractActionStatusPolling();
    }
    );
  }

  function completeExtractStatusPolling(result: IPollExtractActionStatusResult, contentItem: IWorkbookInfo | IDataSourceInfo, actionType: string) {
    const createExtractTasksAction = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
    if(isExtractActionStatusValid(result)) {
      createExtractTasksAction.pollExtractActionStatus(contentItem, actionType, result);
      if (!_.isUndefined(result.pending) && !result.pending) {
        appStore.dispatch(incrementRouterSpaceReactKey());
      }
    }
  }

  function isExtractActionStatusValid(data: IPollExtractActionStatusResult): boolean {
    if(data && data.pending) {
      return false;
    }
    // This failed due to invalid data we want to cancel the polling
    cancelExtractActionStatusPolling();
    return true;
  }

  function pollExtractActionStatus(contentId: string, contentType: string, actionType: string): Promise<any> {
    const extractActionStatusInterval = 10000; // Polling interval of 10 secs
    const pollService = ServiceLocator.get<PollService>('PollService');
    const isComplete = (result: IPollExtractActionStatusResult): boolean => {
      return result && !result.pending;
    };

    statusPoller = pollService.poll(new PollExtractActionStatusRequest({id: contentId, contentType: contentType, actionType: actionType}), isComplete, extractActionStatusInterval);
    return toNativePromise(statusPoller.promise);
  }

  function cancelExtractActionStatusPolling() {
    if(!_.isUndefined(this.statusPoller)) {
      statusPoller.cancel();
    }
  }

  export const ExtractActionStatusService = {
    pollAndUpdateExtractActionStatus
  };

export type ExtractActionStatusService = typeof ExtractActionStatusService;
